import * as React from "react";

import * as client from "../../../client";
import { formatAbbreviatedMoneyExact } from "../Money";
import styles from "./GateExposureDialog.module.css";

// Exact, but capped at 3 decimals (e.g. $5.625MM, $431.589MM). Backend
// _fmt_money matches this.
const formatGateMoney = (dollars: number): string =>
  formatAbbreviatedMoneyExact(dollars, true, 3);

const formatPct = (fraction: number): string => {
  const pct = fraction * 100;
  return pct % 1 === 0 ? `${pct.toFixed(0)}%` : `${pct.toFixed(1)}%`;
};

// The fields GateBar renders. GateTrackingBar (geographic) satisfies this
// structurally; the peril AAL-budget bar maps its own fields onto it.
type GateBarData = {
  gate: string;
  tracking_base: number;
  bound_contribution: number;
  quoted_contribution: number;
  current_layer_contribution: number;
  current_layer_reasoning: string | null;
};

// A gate's full bar = its tracking base. Bound + quoted portfolio fill stacks
// first, then (when shown) the current contribution; ticks mark the escalation
// breakpoints. The orange current elements carry the per-gate reasoning as a
// hover tooltip (rendered once by the parent dialog, targeting [data-gate-current]).
export const GateBar: React.FC<{
  bar: GateBarData;
  breakpoints: client.GateBreakpoint[];
  showCurrent?: boolean;
  currentLabel?: string;
}> = ({
  bar,
  breakpoints,
  showCurrent = true,
  currentLabel = "this layer",
}) => {
  const base = bar.tracking_base;
  const widthPct = (value: number): number =>
    base > 0 ? (value / base) * 100 : 0;
  const current = showCurrent ? bar.current_layer_contribution : 0;
  // Headline utilization counts bound deals plus this layer's live contribution.
  // Quoted deals fill a bar segment but are excluded here — they aren't committed
  // capacity, so they don't drive the gate's headline number.
  const headlineFill = bar.bound_contribution + current;
  const utilization = base > 0 ? headlineFill / base : 0;

  return (
    <div className={styles.gateRow}>
      <div className={styles.gateHeader}>
        <span className={styles.gateName}>{bar.gate}</span>
        <span className={styles.amount}>
          {formatPct(utilization)}
          <span className={styles.grossNote}> of {formatGateMoney(base)}</span>
        </span>
      </div>
      <div className={styles.barTrack}>
        {bar.bound_contribution > 0 && (
          <div
            className={`${styles.segment} ${styles.segBound}`}
            style={{ width: `${widthPct(bar.bound_contribution)}%` }}
          />
        )}
        {bar.quoted_contribution > 0 && (
          <div
            className={`${styles.segment} ${styles.segQuoted}`}
            style={{ width: `${widthPct(bar.quoted_contribution)}%` }}
          />
        )}
        {current > 0 && (
          <div
            className={`${styles.segment} ${styles.segCurrent}`}
            style={{ width: `${widthPct(current)}%` }}
            data-gate-current
            data-pr-tooltip={bar.current_layer_reasoning ?? undefined}
            data-pr-position="top"
          />
        )}
        {breakpoints
          // The 100% mark is the bar's own end, so it isn't ticked.
          .filter((bp) => bp.percent < 1)
          .map((bp) => (
            <div
              key={bp.percent}
              className={styles.tick}
              style={{ left: `${bp.percent * 100}%`, background: bp.color }}
              title={bp.label}
            />
          ))}
      </div>
      <div className={styles.gateMeta}>
        <span>
          <span className={`${styles.swatch} ${styles.segBound}`} />
          {formatGateMoney(bar.bound_contribution)} bound
        </span>
        <span>
          <span className={`${styles.swatch} ${styles.segQuoted}`} />
          {formatGateMoney(bar.quoted_contribution)} quoted
        </span>
        {showCurrent && (
          <span
            className={styles.currentMeta}
            data-gate-current
            data-pr-tooltip={bar.current_layer_reasoning ?? undefined}
            data-pr-position="top"
          >
            <span className={`${styles.swatch} ${styles.segCurrent}`} />
            {formatGateMoney(bar.current_layer_contribution)} {currentLabel}
          </span>
        )}
      </div>
    </div>
  );
};

// Geographic gate bars for one peril family. The per-state EQ gates (the $40M
// contractual hard stops, ~25 of them) are tucked into a collapsed sub-section
// so they don't bury the multi-state named gates listed above them.
export const GeographicGateBars: React.FC<{
  bars: client.GateTrackingBar[];
  breakpoints: client.GateBreakpoint[];
  showCurrent?: boolean;
}> = ({ bars, breakpoints, showCurrent = true }) => {
  const [expanded, setExpanded] = React.useState(false);
  const named = bars.filter((b) => !b.is_per_state);
  const perState = bars.filter((b) => b.is_per_state);
  return (
    <>
      {named.map((bar) => (
        <GateBar
          key={bar.gate}
          bar={bar}
          breakpoints={breakpoints}
          showCurrent={showCurrent}
        />
      ))}
      {perState.length > 0 && (
        <div className={styles.collapsible}>
          <button
            type="button"
            className={styles.collapseToggle}
            onClick={() => setExpanded((prev) => !prev)}
            aria-expanded={expanded}
          >
            <span className={styles.chevron}>{expanded ? "▾" : "▸"}</span>
            Per-state EQ gates ({perState.length})
          </button>
          {expanded &&
            perState.map((bar) => (
              <GateBar
                key={bar.gate}
                bar={bar}
                breakpoints={breakpoints}
                showCurrent={showCurrent}
              />
            ))}
        </div>
      )}
    </>
  );
};

type LossRatioRow = {
  gate: string;
  target: number;
  bound: number | null;
  boundAndQuoted: number | null;
  // Per-layer view only: this layer's pp effect on the bound loss ratio.
  layerEffect?: number | null;
};

const formatLossRatio = (value: number | null): string =>
  value === null ? "—" : formatPct(value);

// Signed pp shift with a direction arrow. Direction/color are redundant: ▲/red
// when it raises the bound loss ratio (worse), ▼/green when it lowers it.
const formatEffect = (effect: number | null): string => {
  if (effect === null) return "—";
  const pp = effect * 100;
  const magnitude = Math.abs(pp);
  // A nonzero shift that rounds to 0.0pp reads as "no change" beside a direction
  // arrow, so mark it "<0.1pp" instead. A true zero has no arrow.
  const roundsToZero = magnitude > 0 && magnitude < 0.05;
  // Fixed-width prefix (arrow slot, space, "<" slot) so the numbers line up in
  // the monospace column whichever variant a row takes.
  const arrow = pp > 0 ? "▲" : pp < 0 ? "▼" : " ";
  const lt = roundsToZero ? "<" : " ";
  const value = roundsToZero ? "0.1pp" : `${magnitude.toFixed(1)}pp`;
  return `${arrow} ${lt}${value}`;
};

const effectColor = (effect: number | null): string | undefined => {
  if (!effect) return undefined; // null/0 → neutral
  return effect > 0 ? "#ef4444" : "#16a34a";
};

// Red when the bound loss ratio is over target.
const overTargetColor = (
  value: number | null,
  target: number
): string | undefined =>
  value !== null && value > target ? "#ef4444" : undefined;

// Marginal pp effect of open quotes on the bound loss ratio:
// LR(bound + quoted) − LR(bound).
const quotesEffect = (row: LossRatioRow): number | null =>
  row.bound === null || row.boundAndQuoted === null
    ? null
    : row.boundAndQuoted - row.bound;

// Per-gate loss ratios: the bound (book) ratio anchored against target, with each
// source's marginal effect on it as a signed pp delta. The per-layer variant adds
// this layer's effect.
const LossRatioTable: React.FC<{
  rows: LossRatioRow[];
  perLayer: boolean;
}> = ({ rows, perLayer }) => (
  <table className={styles.lrTable}>
    <thead>
      <tr>
        <th className={styles.lrColGate}>Peril</th>
        <th className={styles.lrNum}>Bound</th>
        <th className={styles.lrNum}>Δ quotes</th>
        {perLayer && <th className={styles.lrNum}>Δ this layer</th>}
        <th className={styles.lrNum}>Target</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((row) => (
        <tr key={row.gate}>
          <td className={styles.lrColGate}>{row.gate}</td>
          <td
            className={styles.lrNum}
            style={{ color: overTargetColor(row.bound, row.target) }}
          >
            {formatLossRatio(row.bound)}
          </td>
          <td
            className={styles.lrNum}
            style={{ color: effectColor(quotesEffect(row)) }}
          >
            {formatEffect(quotesEffect(row))}
          </td>
          {perLayer && (
            <td
              className={styles.lrNum}
              style={{ color: effectColor(row.layerEffect ?? null) }}
            >
              {formatEffect(row.layerEffect ?? null)}
            </td>
          )}
          <td className={styles.lrNum}>{formatPct(row.target)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

// Map a peril gate's AAL fields onto the GateBar shape: a dollar bar against the
// annual budget. current_aal (present only in the per-layer view) becomes the
// current segment; absent it's 0 and the bar is book-only.
const toBudgetBar = (bar: AalPremiumGateLike): GateBarData => ({
  gate: bar.gate,
  tracking_base: bar.aal_budget,
  bound_contribution: bar.bound_aal,
  quoted_contribution: bar.quoted_aal,
  current_layer_contribution: bar.current_aal ?? 0,
  current_layer_reasoning: null,
});

// The structural shape the client's AalPremiumGateBar satisfies. current_aal and
// loss_ratio.effect_on_bound are populated only in the per-layer (tracking) view.
type AalPremiumGateLike = {
  gate: string;
  aal_budget: number;
  bound_aal: number;
  quoted_aal: number;
  current_aal?: number | null;
  loss_ratio: {
    target: number;
    bound: number | null;
    bound_and_quoted: number | null;
    effect_on_bound?: number | null;
  };
};

// The per-peril AAL-budget bars and loss-ratio table, shared by the book-wide and
// per-layer dialogs. perLayer adds this layer's own AAL segment to each budget bar
// and the "Δ this layer" column to the loss-ratio table.
export const AalPremiumGateSections: React.FC<{
  bars: AalPremiumGateLike[];
  capacityBreakpoints: client.GateBreakpoint[];
  perLayer: boolean;
}> = ({ bars, capacityBreakpoints, perLayer }) => (
  <>
    <div className={styles.section}>
      <div className={styles.sectionTitle}>Per-peril AAL budget</div>
      {bars.map((bar) => (
        <GateBar
          key={bar.gate}
          bar={toBudgetBar(bar)}
          breakpoints={capacityBreakpoints}
          showCurrent={perLayer}
        />
      ))}
    </div>
    <div className={styles.section}>
      <div className={styles.sectionTitle}>Per-peril loss ratio</div>
      {perLayer && (
        <p className={styles.intro}>
          Bound excludes this layer; Δ columns show the pp effect quotes and
          this layer each have on it.
        </p>
      )}
      <LossRatioTable
        perLayer={perLayer}
        rows={bars.map((bar) => ({
          gate: bar.gate,
          target: bar.loss_ratio.target,
          bound: bar.loss_ratio.bound,
          boundAndQuoted: bar.loss_ratio.bound_and_quoted,
          layerEffect: bar.loss_ratio.effect_on_bound,
        }))}
      />
    </div>
  </>
);
